import { queryOptions } from "@tanstack/react-query"

import {
  getAvailabilityFn,
  getDemoPageDataFn,
  getDemoResetStatusFn,
  getMembershipFn,
  getMyBookingFn,
  getMyBookingsFn,
  getStaffBookingsFn,
  getStaffCourseDataFn,
  getStaffCustomersFn,
  getStaffOverviewFn,
  getStaffPaymentsFn,
  getStaffProShopFn,
  getStaffShellDataFn,
  getStaffTeeSheetFn,
  getTeeTimeFn,
  getViewerFn,
} from "@/server/functions"

export const golfDeskQueryKeys = {
  all: ["golf-desk"] as const,
  viewer: () => [...golfDeskQueryKeys.all, "viewer"] as const,
  demo: () => [...golfDeskQueryKeys.all, "demo"] as const,
  demoReset: () => [...golfDeskQueryKeys.all, "demo-reset"] as const,
  availability: (course?: string, date?: string) =>
    [
      ...golfDeskQueryKeys.all,
      "availability",
      course ?? null,
      date ?? null,
    ] as const,
  teeTime: (teeTimeId: string) =>
    [...golfDeskQueryKeys.all, "tee-time", teeTimeId] as const,
  myBookings: () => [...golfDeskQueryKeys.all, "my-bookings"] as const,
  myBooking: (bookingId: string) =>
    [...golfDeskQueryKeys.myBookings(), bookingId] as const,
  membership: () => [...golfDeskQueryKeys.all, "membership"] as const,
  staff: () => [...golfDeskQueryKeys.all, "staff"] as const,
  staffShell: () => [...golfDeskQueryKeys.staff(), "shell"] as const,
  staffOverview: (date?: string) =>
    [...golfDeskQueryKeys.staff(), "overview", date ?? null] as const,
  staffTeeSheet: (course?: string, date?: string) =>
    [
      ...golfDeskQueryKeys.staff(),
      "tee-sheet",
      course ?? null,
      date ?? null,
    ] as const,
  staffBookings: (query?: string, status?: string) =>
    [
      ...golfDeskQueryKeys.staff(),
      "bookings",
      query ?? null,
      status ?? null,
    ] as const,
  staffCustomers: () => [...golfDeskQueryKeys.staff(), "customers"] as const,
  staffProShop: (date?: string, page = 1) =>
    [...golfDeskQueryKeys.staff(), "pro-shop", date ?? null, page] as const,
  staffCourse: () => [...golfDeskQueryKeys.staff(), "course"] as const,
  staffPayments: () => [...golfDeskQueryKeys.staff(), "payments"] as const,
}

export function viewerQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.viewer(),
    queryFn: () => getViewerFn(),
  })
}

export function demoPageQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.demo(),
    queryFn: () => getDemoPageDataFn(),
  })
}

export function demoResetQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.demoReset(),
    queryFn: () => getDemoResetStatusFn(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export function availabilityQueryOptions({
  course,
  date,
}: {
  course?: string
  date?: string
}) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.availability(course, date),
    queryFn: () =>
      getAvailabilityFn({
        data: { courseSlug: course, date },
      }),
    // Capacity is still revalidated when checkout starts. Keeping recently
    // prefetched days fresh for one minute makes date changes instant without
    // weakening the booking write path.
    staleTime: 60_000,
  })
}

export function teeTimeQueryOptions(teeTimeId: string) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.teeTime(teeTimeId),
    queryFn: () => getTeeTimeFn({ data: { teeTimeId } }),
  })
}

export function myBookingsQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.myBookings(),
    queryFn: () => getMyBookingsFn(),
  })
}

export function myBookingQueryOptions(bookingId: string) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.myBooking(bookingId),
    queryFn: () => getMyBookingFn({ data: { bookingId } }),
  })
}

export function membershipQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.membership(),
    queryFn: () => getMembershipFn(),
  })
}

export function staffShellQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffShell(),
    queryFn: () => getStaffShellDataFn(),
  })
}

export function staffOverviewQueryOptions(date?: string) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffOverview(date),
    queryFn: () => getStaffOverviewFn({ data: { date } }),
  })
}

export function staffTeeSheetQueryOptions({
  course,
  date,
}: {
  course?: string
  date?: string
}) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffTeeSheet(course, date),
    queryFn: () =>
      getStaffTeeSheetFn({
        data: { courseSlug: course, date },
      }),
  })
}

export function staffBookingsQueryOptions({
  query,
  status,
}: {
  query?: string
  status?: string
}) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffBookings(query, status),
    queryFn: () =>
      getStaffBookingsFn({
        data: { query, status },
      }),
  })
}

export function staffCustomersQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffCustomers(),
    queryFn: () => getStaffCustomersFn(),
  })
}

export function staffProShopQueryOptions(date?: string, page = 1) {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffProShop(date, page),
    queryFn: () => getStaffProShopFn({ data: { date, page } }),
  })
}

export function staffCourseQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffCourse(),
    queryFn: () => getStaffCourseDataFn(),
  })
}

export function staffPaymentsQueryOptions() {
  return queryOptions({
    queryKey: golfDeskQueryKeys.staffPayments(),
    queryFn: () => getStaffPaymentsFn(),
  })
}
